import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthResponse } from '../types';
import { authService } from '../services/authService';
import { setActingOrg } from '../services/actingOrg';

interface AuthContextType {
  user: AuthResponse | null;
  login: (token: string, userData: AuthResponse) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthResponse | null>(() => {
    // Initialize from localStorage on mount
    return authService.getCurrentUser();
  });

  useEffect(() => {
    // Sync user state with localStorage
    const currentUser = authService.getCurrentUser();
    if (currentUser && !user) {
      setUser(currentUser);
    }
  }, [user]);

  const login = (token: string, userData: AuthResponse) => {
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(userData));
    // 清除上一个账号可能残留的代入态，避免普通 admin 继承 superadmin 的 acting_org。
    setActingOrg(null);
    setUser(userData);
    // 触发品牌按新账号重新拉取（清掉上个账号残留的品牌名/logo/主色）。
    window.dispatchEvent(new Event('acting-org-changed'));
  };

  const logout = () => {
    authService.logout();
    // 退出时清除代入态。
    setActingOrg(null);
    setUser(null);
    // 退出后品牌回退默认并移除 --brand-primary CSS 变量（refreshBranding 在无 token 时清理）。
    window.dispatchEvent(new Event('acting-org-changed'));
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    // SUPER_ADMIN 也能进入后台门户（拥有 ADMIN 的全部能力 + 机构管理）
    isAdmin: user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN',
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
